import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Sparkles, Loader2, AlertCircle } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const MAX_WORDS = 200;
const wordCount = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject_type: "feedback", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const handleMessage = (e) => {
    const v = e.target.value;
    if (wordCount(v) <= MAX_WORDS) {
      setForm({ ...form, message: v });
    } else {
      // Trim to first MAX_WORDS words
      const trimmed = v.trim().split(/\s+/).slice(0, MAX_WORDS).join(" ");
      setForm({ ...form, message: trimmed });
      toast.error(`Message capped at ${MAX_WORDS} words`);
    }
  };

  // Block paste of files (images / videos): the textarea already only accepts text,
  // but we also drop any non-text dataTransfer payloads for clarity.
  const blockMediaDrop = (e) => {
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      e.preventDefault();
      toast.error("Only text messages are allowed — images and videos cannot be attached.");
    }
  };

  const words = useMemo(() => wordCount(form.message), [form.message]);
  const remaining = MAX_WORDS - words;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Please fill in all fields (message ≥ 5 chars)");
      return;
    }
    if (words > MAX_WORDS) {
      toast.error(`Message must be ${MAX_WORDS} words or fewer`);
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — your message has been sent");
      setForm({ name: "", email: "", subject_type: "feedback", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24" data-testid="contact-page">
      <div className="grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-neutral-300 border border-white/15 px-3 py-1.5 bg-white/5">
            <Sparkles className="w-3 h-3 text-[#F4A22A]" /> Powered by AI
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-6xl text-white mt-6">
            Got something to say?
          </h1>
          <p className="text-neutral-400 mt-5 max-w-md">
            Send us feedback, suggestions, or a support request. We read every message.
          </p>

          <div className="mt-10 space-y-5 text-sm">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-black border border-white/10 flex items-center justify-center"><Mail className="w-4 h-4 text-[#F4A22A]" /></div>
              <div>
                <div className="text-neutral-500 text-[11px] uppercase tracking-[0.2em]">Email</div>
                <a href="mailto:technosort.rd@gmail.com" className="text-white hover:text-[#F4A22A] transition-colors" data-testid="contact-email-link">technosort.rd@gmail.com</a>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 card-tile p-10" data-testid="contact-form">
          <div className="font-display font-black text-2xl text-white mb-2">Send us a message</div>
          <p className="text-neutral-400 text-sm mb-8">Text only — up to {MAX_WORDS} words. Feedback, suggestions, support — all welcome.</p>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-400 mb-2">Name</label>
              <input
                value={form.name} onChange={set("name")}
                data-testid="contact-input-name"
                required
                className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-[#F4A22A]"
              />
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-400 mb-2">Email</label>
              <input
                value={form.email} onChange={set("email")}
                type="email" required
                data-testid="contact-input-email"
                className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-[#F4A22A]"
              />
            </div>
          </div>

          <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-400 mb-2 mt-5">Type</label>
          <Select value={form.subject_type} onValueChange={(v) => set("subject_type")(v)}>
            <SelectTrigger className="bg-black border border-white/20 text-white rounded-none h-12 focus:ring-0 focus:border-[#F4A22A]" data-testid="contact-select-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-white/15 text-white rounded-none">
              <SelectItem value="feedback" data-testid="contact-type-feedback">Feedback</SelectItem>
              <SelectItem value="suggestion" data-testid="contact-type-suggestion">Suggestion</SelectItem>
              <SelectItem value="support" data-testid="contact-type-support">Support</SelectItem>
              <SelectItem value="sales" data-testid="contact-type-sales">Sales / Buying</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center justify-between mt-5 mb-2">
            <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-400">Message (text only)</label>
            <span
              data-testid="contact-word-counter"
              className={`text-[11px] tracking-wide ${remaining < 0 ? "text-[#C8372D]" : remaining < 20 ? "text-[#F4A22A]" : "text-neutral-500"}`}
            >
              {words} / {MAX_WORDS} words
            </span>
          </div>
          <textarea
            value={form.message}
            onChange={handleMessage}
            onDrop={blockMediaDrop}
            rows={6} required
            placeholder="Type your message here (no images or videos — text only, up to 200 words)."
            data-testid="contact-input-message"
            className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-[#F4A22A] resize-none"
          />
          <div className="mt-2 text-[11px] text-neutral-500 inline-flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" /> No file attachments — images and videos are not accepted.
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="btn-primary mt-8 w-full py-4 text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send message
          </button>

          <div className="mt-6 text-[11px] text-neutral-500">
            Your message will be delivered to <span className="text-neutral-300">technosort.rd@gmail.com</span>.
          </div>
        </form>
      </div>
    </section>
  );
}
